/**
 * The Routines card (SPEC.md §4.6, §7.1, §8.7).
 *
 * Fetches its own data from `/api/recurring`, the same pattern Notes and Blackjack already
 * use rather than riding the `/api/briefing` payload — recurrence is its own subsystem with
 * its own endpoints (§10).
 *
 * Default: occurrences due today, plus anything overdue. An expand control reveals
 * occurrences that are not due yet, so one can be completed early — each recurring task has
 * exactly one live occurrence (§7.1.2), so this is one row per task, never several dates
 * forecast for the same task, and a task shows in exactly one of the two lists, never both.
 *
 * The tick is the one true complete/undo control — the same button, same meaning, as every
 * other task row in EFSS. There is no separate "Complete" button. Completed and skipped rows
 * follow the §8.5 hold-in-place rule: they stay where they were ticked until the next load,
 * with the tick as its own undo. Undo can be refused by the server once something has moved
 * on since (DECISIONS.md D90) — the error is surfaced rather than silently retried.
 *
 * A recurring task is excluded from the regular Tasks list (DECISIONS.md D91) — it has no
 * other row anywhere to edit or delete it from, so this card carries its own edit and
 * delete, reusing `TaskEditor` inline exactly the way an ordinary task row would.
 *
 * Streaks and the rolling rate are shown small, deliberately: "never a headline number,
 * never a best ever" (§8.7).
 */

import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type RecurrenceRequest,
  type RecurringEntry,
  type Task,
  type TaskHistory,
} from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { postNotice } from "../notices";
import { queueOccurrenceComplete, queueOccurrenceSkip } from "../taskQueue";
import { Calendar, History as HistoryIcon, Pencil, Repeat, SkipArc, Trash } from "./icons";
import { BadgeGlossary, type GlossaryEntry } from "./BadgeGlossary";
import { DeltaHistogram } from "./DeltaHistogram";
import { Die } from "./Die";
import { OverflowMenu, type OverflowMenuItem } from "./OverflowMenu";
import { RecentOccurrences } from "./RecentOccurrences";
import { StreakBadge } from "./StreakBadge";
import { TaskEditor, type TaskPatch } from "./TaskEditor";
import { TaskGlow } from "./TaskGlow";
import { celebrate, useFireworks } from "./fireworksContext";
import { energyTitle, recurrenceLabel, relativeDateLabel } from "./taskFormat";
import { todayLocalDate } from "./recurrence";
import { useSettings } from "../settings";

const GLOSSARY: GlossaryEntry[] = [
  {
    key: "repeat",
    title: "Repeat rule",
    preview: (
      <span className="badge repeat">
        <Repeat /> every day
      </span>
    ),
    description: "How often this comes around, in plain English.",
  },
  {
    key: "energy",
    title: "Energy",
    preview: (
      <span className="badge energy">
        <Die value={5} title={energyTitle(5)} />
      </span>
    ),
    description: "Activation cost, 1 to 5 - how much this asks of you.",
  },
  {
    key: "due",
    title: "Due date",
    preview: (
      <span className="badge">
        <Calendar /> Today
      </span>
    ),
    description:
      "When it's due. Yesterday, Today, Tomorrow, and In N days / N days ago for about a " +
      "week either side, then the plain date. Red means overdue.",
  },
  {
    key: "rate",
    title: "Completion rate",
    preview: <span className="badge">80%</span>,
    description:
      "Rolling 30-day completion rate. Skips don't count against it. - means there's " +
      "nothing in the window yet to judge.",
  },
  {
    key: "streak",
    title: "Streak",
    preview: <span className="badge">×3</span>,
    description:
      "Consecutive completions, most recent first. A skip doesn't break it; a missed one does.",
  },
];

type HeldOutcome = {
  entry: RecurringEntry;
  status: "completed" | "skipped";
  /** Which list it was acted on from, so it holds its place there and nowhere else. */
  origin: "today" | "upcoming";
};

function ratePercent(rate: number | null): string | null {
  return rate === null ? null : `${Math.round(rate * 100)}%`;
}

/**
 * `held` and each row's React key are keyed on this, not on `task_id` alone.
 *
 * Completing or skipping advances the task to a *new* pending occurrence — a fresh
 * `due_date` for the same `task_id` — which then shows up as its own live row on the next
 * load, per §7.1.2's "exactly one live occurrence per task". Keying by `task_id` alone made
 * that new row inherit the just-ticked outcome (same key, wrong occurrence) and made undo
 * target the new occurrence's `due_date` instead of the one actually resolved. Since
 * `(task_id, due_date)` is the row's real identity in `task_occurrences`, this composite key
 * can never collide between a held (resolved) row and a live (pending) one.
 */
function occKey(taskId: string, dueDate: string): string {
  return `${taskId}::${dueDate}`;
}

/**
 * How the today and upcoming lists render: next due date first, same as anywhere else in
 * EFSS a due date sorts a list — with one deliberate exception.
 *
 * Completing or skipping advances a task to a new occurrence (§7.1.2), and that new
 * occurrence's due date is usually *not* the one the held row had — a daily task ticked
 * today next comes due tomorrow, so the fresh occurrence is fetched into `upcoming`, not
 * `entries`, even though the row that was just ticked is sitting in the *today* list. Pairing
 * within one list only (today's held with today's live, upcoming's with upcoming's) missed
 * exactly this, the common case, and left the fresh row to sort by its own date whichever
 * list it landed in — sometimes with an unrelated task's row between it and the one just
 * ticked.
 *
 * So the pairing runs once across both lists together: a held row's fresh sibling — found by
 * `task_id`, wherever it actually is — is pinned immediately after it and rendered in the
 * *held* row's own list, not the sibling's own window. Everything else sorts by due date
 * within whichever list it belongs to. When a task has been ticked more than once this
 * session (each held separately, its own undo), only the most recent hold claims the fresh
 * row — an older still-held completion for the same task sorts on its own date, undisturbed.
 */
function orderRows(
  todayLive: RecurringEntry[],
  upcomingLive: RecurringEntry[],
  heldRows: HeldOutcome[],
): { today: RecurringEntry[]; upcoming: RecurringEntry[] } {
  const liveByTask = new Map<string, RecurringEntry>();
  for (const entry of todayLive) liveByTask.set(entry.task_id, entry);
  for (const entry of upcomingLive) liveByTask.set(entry.task_id, entry);

  // Last write wins, so a task ticked twice this session ends with its most recent hold
  // here — `heldRows` walks in the order things were ticked (object keys keep insertion
  // order), and this reads the last one written for each task.
  const latestHeldByTask = new Map<string, RecurringEntry>();
  for (const h of heldRows) latestHeldByTask.set(h.entry.task_id, h.entry);

  type Unit = {
    origin: "today" | "upcoming";
    sortDate: string;
    sortTitle: string;
    rows: RecurringEntry[];
  };
  const units: Unit[] = [];
  const claimed = new Set<string>();

  for (const h of heldRows) {
    const isLatestHold = latestHeldByTask.get(h.entry.task_id) === h.entry;
    const sibling = isLatestHold ? liveByTask.get(h.entry.task_id) : undefined;
    units.push({
      origin: h.origin,
      sortDate: h.entry.due_date,
      sortTitle: h.entry.title,
      rows: sibling ? [h.entry, sibling] : [h.entry],
    });
    if (sibling) claimed.add(occKey(sibling.task_id, sibling.due_date));
  }

  for (const [origin, live] of [
    ["today", todayLive],
    ["upcoming", upcomingLive],
  ] as const) {
    for (const entry of live) {
      if (claimed.has(occKey(entry.task_id, entry.due_date))) continue;
      units.push({ origin, sortDate: entry.due_date, sortTitle: entry.title, rows: [entry] });
    }
  }

  const ordered = (origin: "today" | "upcoming") =>
    units
      .filter((u) => u.origin === origin)
      .sort((a, b) => a.sortDate.localeCompare(b.sortDate) || a.sortTitle.localeCompare(b.sortTitle))
      .flatMap((u) => u.rows);

  return { today: ordered("today"), upcoming: ordered("upcoming") };
}

export function RecurringCard({
  refreshKey,
  onChange,
}: {
  /**
   * Bumped whenever the briefing around this card reloads — the same convention
   * `GoneQuiet`/`TaskDrawer` use. Without it, a task created or edited elsewhere (the
   * QuickAdd form, an inbox resolve, the regular task editor) never appeared here until a
   * full page reload, since this card fetches its own data independently of the briefing.
   */
  refreshKey?: number;
  /**
   * Called after completing, skipping, or undoing an occurrence.
   *
   * This card fetches its own data (§10) and has no other way to tell the briefing around
   * it that something changed — without this, completing or undoing a routine here never
   * touched Wins, which reads the same `/api/briefing` payload Briefing.tsx's own `complete`
   * and `undo` already refresh after an ordinary task. Reusing that same `load` as the
   * callback is what keeps a routine's completion counted alongside every other one (§8.7:
   * "Recurring completions appear in Wins like any other completed task").
   */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects } = useAreasAndProjects(refreshKey);
  const settings = useSettings();
  const [entries, setEntries] = useState<RecurringEntry[] | null>(null);
  const [today, setToday] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [upcoming, setUpcoming] = useState<RecurringEntry[]>([]);
  const [held, setHeld] = useState<Record<string, HeldOutcome>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [history, setHistory] = useState<TaskHistory | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const burst = useFireworks();

  const load = useCallback(async () => {
    try {
      const body = await api.recurring("today");
      setEntries(body.occurrences);
      setToday(body.date);
    } catch {
      setEntries((current) => current ?? []);
    }
  }, []);

  const loadUpcoming = useCallback(async () => {
    try {
      setUpcoming((await api.recurring("upcoming")).occurrences);
    } catch {
      setUpcoming([]);
    }
  }, []);

  const reload = useCallback(
    () => Promise.all([load(), expanded ? loadUpcoming() : Promise.resolve()]),
    [load, loadUpcoming, expanded],
  );

  // On mount, and whenever something elsewhere in the briefing changes — a task created,
  // edited, or resolved from the inbox all bump `refreshKey` (Briefing.tsx's `revision`),
  // the same signal GoneQuiet and TaskDrawer already key off. Without this a newly-recurring
  // task sat invisible here until a full page reload, since this card's data comes from its
  // own fetch rather than the briefing payload.
  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    if (expanded) void loadUpcoming();
  }, [expanded, loadUpcoming, refreshKey]);

  async function complete(
    entry: RecurringEntry,
    origin: "today" | "upcoming",
    at?: { x: number; y: number },
  ) {
    // §8.4: completion is a celebration, once per point of activation cost — the same
    // burst any other task gets. Fired before the reload so it happens where the row was,
    // not where the list ends up (Briefing.tsx's `complete` does the same).
    if (at) celebrate(burst, at.x, at.y, entry.energy);
    const key = occKey(entry.task_id, entry.due_date);
    const completedDate = todayLocalDate();
    setBusy(key);
    try {
      // The completion date is always stated, never left for the server to fill in from
      // whenever it happens to process this (D164). Omitting it means `record_completion`
      // uses *its* today — so a tick at 23:59:59 that lands at 00:00:01 files the work on
      // the wrong day and takes `delta_days` and the streak with it. The "fix" control under
      // the history unfold has always sent an explicit date (`RecentOccurrences.tsx`); this
      // is the same request, so it says the same thing.
      await api.completeOccurrence(entry.task_id, entry.due_date, undefined, completedDate);
      setHeld((current) => ({ ...current, [key]: { entry, status: "completed", origin } }));
      await Promise.all([reload(), onChange?.()]);
    } catch (err) {
      if (!(err instanceof ApiError && err.isOffline)) throw err;
      // Safe to replay precisely because the date above is pinned — see the `occurrence-*`
      // note in `taskQueue.ts` for why a repeat neither skips a cycle nor double-counts.
      await queueOccurrenceComplete(entry.task_id, entry.due_date, completedDate);
      postNotice(`"${entry.title}" is queued - it'll complete once you're back online.`);
    } finally {
      setBusy(null);
    }
  }

  async function skip(entry: RecurringEntry, origin: "today" | "upcoming") {
    const key = occKey(entry.task_id, entry.due_date);
    setBusy(key);
    try {
      await api.skipOccurrence(entry.task_id, entry.due_date);
      setHeld((current) => ({ ...current, [key]: { entry, status: "skipped", origin } }));
      await Promise.all([reload(), onChange?.()]);
    } catch (err) {
      if (!(err instanceof ApiError && err.isOffline)) throw err;
      await queueOccurrenceSkip(entry.task_id, entry.due_date);
      postNotice(`"${entry.title}" is queued to skip - it'll apply once you're back online.`);
    } finally {
      setBusy(null);
    }
  }

  async function undo(entry: RecurringEntry) {
    const key = occKey(entry.task_id, entry.due_date);
    setBusy(key);
    setUndoError(null);
    try {
      await api.reopenOccurrence(entry.task_id, entry.due_date);
      setHeld((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      await Promise.all([reload(), onChange?.()]);
    } catch (err) {
      // A later occurrence may have already been resolved (DECISIONS.md D90) — say so
      // rather than leaving the tick looking like it did nothing.
      setUndoError(err instanceof ApiError ? err.message : "Could not undo that.");
    } finally {
      setBusy(null);
    }
  }

  // Fetching is left to the effect above, keyed on `expanded` — toggling here is enough to
  // trigger it, and keeps this from racing a `refreshKey` bump that lands mid-toggle.
  function toggleUpcoming() {
    setExpanded((current) => !current);
  }

  const toggleHistory = useCallback(
    async (taskId: string) => {
      if (historyFor === taskId) {
        setHistoryFor(null);
        return;
      }
      setHistoryFor(taskId);
      setHistory(null);
      try {
        setHistory(await api.taskHistory(taskId));
      } catch {
        setHistory(null);
      }
    },
    [historyFor],
  );

  // After `RecentOccurrences` reclaims a missed occurrence (D140): the panel's own history
  // needs the fresh outcome, and `reload`/`onChange` because a reclaim also moves the rate
  // and streak badges shown outside the panel, and — completed — counts in Wins (§8.7).
  async function historyFixed(taskId: string) {
    try {
      setHistory(await api.taskHistory(taskId));
    } catch {
      /* the panel just keeps showing what it had until the next open */
    }
    await Promise.all([reload(), onChange?.()]);
  }

  async function startEdit(taskId: string) {
    try {
      setEditing(await api.task(taskId));
    } catch {
      /* the row just stays as it was — nothing to edit if the fetch failed */
    }
  }

  async function saveEdit(patch: TaskPatch, recurrence?: RecurrenceRequest) {
    if (!editing) return;
    if (Object.keys(patch).length > 0) await api.patchTask(editing.id, patch);
    if (recurrence !== undefined) await api.setRecurrence(editing.id, recurrence);
    setEditing(null);
    await reload();
  }

  async function removeTask(taskId: string, title: string) {
    if (
      !window.confirm(
        `Permanently delete "${title}"?\n\n` +
          "This cannot be undone. Anything it already did stays in your history either way.",
      )
    ) {
      return;
    }
    await api.deleteTask(taskId);
    setEditing(null);
    await reload();
  }

  if (entries === null) return null;

  // Held rows stay visible in place until the next load (§8.5's rule) — a row already gone
  // from both live lists (resolved elsewhere, or its task deleted) is dropped. Keyed on
  // `(task_id, due_date)`: the occurrence that was just resolved advances the task to a new
  // pending `due_date`, so the held row's own key never collides with the fresh live one.
  const liveKeys = new Set([...entries, ...upcoming].map((e) => occKey(e.task_id, e.due_date)));
  const heldRows = Object.values(held).filter(
    (h) => !liveKeys.has(occKey(h.entry.task_id, h.entry.due_date)),
  );
  // Nothing due today used to mean the whole card vanished — including the "show upcoming"
  // toggle below, the only way to complete something early or reach the edit/delete controls
  // (§4.6, this card's own docstring: recurring tasks carry no other row anywhere). That made
  // an empty today genuinely block routine management, not just quiet it down.
  const clearForToday = entries.length === 0 && heldRows.length === 0;

  function row(entry: RecurringEntry, options: { origin: "today" | "upcoming" } = { origin: "today" }) {
    if (editing && editing.id === entry.task_id) {
      return (
        <TaskEditor
          key={entry.task_id}
          task={editing}
          areas={areas}
          projects={projects}
          onCancel={() => setEditing(null)}
          onSave={saveEdit}
          onDelete={(task) => removeTask(task.id, task.title)}
        />
      );
    }

    // Not which list the row happens to render in — a fresh occurrence pinned into the
    // today list right after the row it replaced (see `orderRows`) can genuinely not be due
    // yet, and "can this be skipped" has to answer for the occurrence itself either way.
    const early = today !== null && entry.due_date > today;
    const key = occKey(entry.task_id, entry.due_date);
    const outcome = held[key];
    const done = Boolean(outcome);
    // A skipped row already had a working undo — the tick's `onClick` never distinguished
    // the two statuses — but it looked exactly like a completed one (green check,
    // strikethrough), so there was nothing to notice or click. Its own icon, colour and
    // label make the undo discoverable instead of merely present.
    const skipped = outcome?.status === "skipped";
    const rate = ratePercent(entry.rate_30d);

    return (
      <li key={key} className={`task${done ? (skipped ? " skipped" : " done") : ""}`}>
        <TaskGlow energy={entry.energy} overdue={entry.overdue && !done} important={false} />
        <button
          className="tick"
          onClick={
            done
              ? () => void undo(entry)
              : (event) => {
                  // Where the burst should go: the row's midline, matching TaskList.tsx's
                  // TaskRow so a completion reads the same wherever it happens.
                  const box = event.currentTarget.closest("li")!.getBoundingClientRect();
                  const at = {
                    x: box.left + Math.min(box.width * 0.4, 220),
                    y: box.top + box.height / 2,
                  };
                  void complete(entry, options.origin, at);
                }
          }
          disabled={busy === key}
          aria-label={skipped ? `Undo skip: ${entry.title}` : done ? `Undo: ${entry.title}` : `Complete: ${entry.title}`}
          title={
            skipped
              ? "Skipped - recorded, not scored. Click to undo."
              : done
                ? "Done. Click to put it back."
                : early
                  ? "Completing early records the delta against this occurrence (§8.7)"
                  : "Complete"
          }
        >
          {skipped ? <SkipArc /> : done ? "✓" : ""}
        </button>

        <span className="title">
          <span className="text">{entry.title}</span>
          <span className="meta">
            <span
              className="badge repeat"
              title="This is the occurrence itself - complete or skip it right here"
            >
              <Repeat /> {recurrenceLabel(entry.rrule)}
            </span>
            <span className="badge energy" title={energyTitle(entry.energy)}>
              <Die value={entry.energy} title={energyTitle(entry.energy)} />
            </span>
            <span
              className={`badge${entry.overdue && !done ? " overdue" : ""}`}
              title={`${entry.overdue && !done ? "Overdue" : "Due"} ${entry.due_date}`}
            >
              <Calendar /> {today ? relativeDateLabel(entry.due_date, today) : entry.due_date}
            </span>
            <span className="badge" title="Rolling 30-day completion rate - skips excluded">
              {rate ?? "-"}
            </span>
            <StreakBadge taskId={entry.task_id} streak={entry.streak} capped={entry.streak_capped} />
          </span>
        </span>

        <span className="task-actions">
          {/* Skip is the daily-use action, so it stays inline in both modes — the same
              reasoning that keeps Star inline on a Task row (§11). */}
          {!done && !early && (
            <button
              className="btn ghost small"
              disabled={busy === key}
              onClick={() => void skip(entry, options.origin)}
              aria-label={`Skip: ${entry.title}`}
              title="Recorded, not scored - for when it happened without you (§7.1.3)"
            >
              <SkipArc />
            </button>
          )}
          {settings.cardMode === "plan" ? (
            <>
              <button
                className="btn ghost small"
                onClick={() => void startEdit(entry.task_id)}
                aria-label={`Edit: ${entry.title}`}
                title="Edit"
              >
                <Pencil />
              </button>
              <button
                className="btn ghost small danger"
                onClick={() => void removeTask(entry.task_id, entry.title)}
                aria-label={`Delete: ${entry.title}`}
                title="Delete permanently"
              >
                <Trash />
              </button>
              <button
                className="btn ghost small"
                onClick={() => void toggleHistory(entry.task_id)}
                aria-expanded={historyFor === entry.task_id}
                aria-label={
                  historyFor === entry.task_id ? `Hide history: ${entry.title}` : `History: ${entry.title}`
                }
                title="History"
              >
                <HistoryIcon />
              </button>
            </>
          ) : (
            <OverflowMenu
              label={`More actions: ${entry.title}`}
              items={
                [
                  { key: "edit", label: "Edit", icon: <Pencil />, onClick: () => void startEdit(entry.task_id) },
                  {
                    key: "history",
                    label: historyFor === entry.task_id ? "Hide history" : "History",
                    icon: <HistoryIcon />,
                    onClick: () => void toggleHistory(entry.task_id),
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    icon: <Trash />,
                    onClick: () => void removeTask(entry.task_id, entry.title),
                    danger: true,
                  },
                ] satisfies OverflowMenuItem[]
              }
            />
          )}
        </span>

        {historyFor === entry.task_id && (
          <div className="recurring-history">
            {history ? (
              <>
                <DeltaHistogram history={history} />
                <RecentOccurrences
                  taskId={entry.task_id}
                  history={history}
                  onFixed={() => historyFixed(entry.task_id)}
                />
              </>
            ) : (
              <p className="small muted">Loading…</p>
            )}
          </div>
        )}
      </li>
    );
  }

  // One pairing pass across both lists together — see `orderRows` for why: a held row's
  // fresh sibling usually surfaces in the *other* window, not its own.
  const ordered = orderRows(entries, upcoming, heldRows);

  return (
    <section className="panel">
      <header>
        <h2>Routines</h2>
        <span className="row">
          <span className="muted small" title="Due today, plus anything overdue">
            {entries.length} {entries.length === 1 ? "routine" : "routines"}
          </span>
          <BadgeGlossary entries={GLOSSARY} />
        </span>
      </header>

      {undoError && (
        <p className="small" style={{ margin: "0 0 0.5rem", color: "var(--warn)" }}>
          {undoError}
        </p>
      )}

      {clearForToday ? (
        <div className="empty">
          <strong>Nothing due today - nice work.</strong>
        </div>
      ) : (
        <ul className="tasks recurring-list">
          {ordered.today.map((entry) => row(entry, { origin: "today" }))}
        </ul>
      )}

      <button type="button" className="btn ghost small recurring-expand" onClick={toggleUpcoming}>
        {expanded ? "Hide upcoming" : "Show upcoming"}
      </button>

      {expanded && (
        <ul className="tasks recurring-upcoming">
          {ordered.upcoming.length === 0 ? (
            <p className="small muted">Nothing further out yet.</p>
          ) : (
            ordered.upcoming.map((entry) => row(entry, { origin: "upcoming" }))
          )}
        </ul>
      )}
    </section>
  );
}
